// Gestionar (warehouse manager) task board + repack tool. Lists warehouse tasks
// (auto-created from reserves/orders + manual), and lets the gestionar execute a
// REPACK: take coils from several pallets → one new pallet (mixed products
// allowed). Repacking updates stock so the warehouse mirrors physical reality.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, Badge, Button, Modal, Form, Table, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxesStacked, faPlay, faCheck, faXmark, faRotate } from '@fortawesome/free-solid-svg-icons';
import { API } from '../../config/api';

const ENG = axios.create({ baseURL: API.BACKEND, timeout: 12000 });
ENG.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});

const STATUS_BG = { pending: 'warning', in_progress: 'info', done: 'success', cancelled: 'secondary' };

export default function WarehouseTasks({ title = 'Warehouse Tasks' }) {
  const { data: tasks = [], refetch, isFetching } = useQuery({
    queryKey: ['warehouse-tasks'],
    queryFn: async () => (await ENG.get('/api/v1/warehouse/tasks')).data?.data || [],
    refetchInterval: 15000
  });
  const { data: pallets = [], refetch: refetchPallets } = useQuery({
    queryKey: ['warehouse-stock-pallets'],
    queryFn: async () =>
      (await ENG.get('/api/v1/warehouse/stock?type=europallet')).data?.data || [],
    staleTime: 10000
  });

  const [repackOpen, setRepackOpen] = useState(false);
  const [repackTask, setRepackTask] = useState(null);
  const [take, setTake] = useState({}); // serial -> coils to take
  const [destName, setDestName] = useState('');
  const [destOrderId, setDestOrderId] = useState('');
  const [destOrderCode, setDestOrderCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  const flash = (v, m) => { setBanner({ v, m }); setTimeout(() => setBanner(null), 4000); };

  const pending = useMemo(() => tasks.filter(t => t.status !== 'done'), [tasks]);

  const openRepack = task => {
    setRepackTask(task || null);
    const t = {};
    if (task?.sources?.length) task.sources.forEach(s => { t[s.serial] = s.coils || 0; });
    setTake(t);
    setDestName(task?.customerName || '');
    setDestOrderId(task?.forOrderId || '');
    setDestOrderCode(task?.forOrderCode || '');
    setRepackOpen(true);
  };

  const setStatus = async (task, status) => {
    try {
      await ENG.patch(`/api/v1/warehouse/tasks/${task.code}`, { status });
      refetch();
    } catch (e) { flash('danger', e.response?.data?.error || e.message); }
  };

  const doRepack = async () => {
    const sources = Object.entries(take)
      .map(([serial, coils]) => ({ serial, coils: Number(coils) || 0 }))
      .filter(s => s.coils > 0);
    if (!sources.length) { flash('danger', 'Pick coils from at least one pallet'); return; }
    setBusy(true);
    try {
      const res = await ENG.post('/api/v1/warehouse/repack', {
        sources,
        customerName: destName || null,
        salesOrderId: destOrderId || null,
        salesOrderCode: destOrderCode || null,
        taskCode: repackTask?.code || null
      });
      flash('success', `New pallet ${res.data?.data?.newPallet?.serial} created`);
      setRepackOpen(false);
      refetch();
      refetchPallets();
    } catch (e) {
      flash('danger', e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const takenTotal = Object.values(take).reduce((a, c) => a + (Number(c) || 0), 0);

  return (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faBoxesStacked} className="text-primary" />
          <strong>{title}</strong>
          <Badge bg="warning" text="dark">{pending.length} to do</Badge>
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="link" className="text-body" onClick={() => refetch()}>
            <FontAwesomeIcon icon={faRotate} spin={isFetching} />
          </Button>
          <Button size="sm" variant="primary" onClick={() => openRepack(null)}>
            <FontAwesomeIcon icon={faBoxesStacked} className="me-1" /> New repack
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {banner && <Alert variant={banner.v} className="py-2">{banner.m}</Alert>}
        {tasks.length === 0 ? (
          <div className="text-body-tertiary">No warehouse tasks.</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {tasks.map(t => (
              <div key={t.code} className="border rounded p-2 d-flex align-items-start justify-content-between gap-2 flex-wrap">
                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge bg={STATUS_BG[t.status] || 'secondary'} className="text-uppercase">{t.status}</Badge>
                    <span className="font-monospace fs-10 text-body-tertiary">{t.code}</span>
                    {t.auto && <Badge bg="secondary">auto</Badge>}
                    <span className="fw-semibold">{t.title || t.type}</span>
                  </div>
                  {t.description && <div className="fs-9 text-body-secondary mt-1">{t.description}</div>}
                  {t.sources?.length > 0 && (
                    <div className="fs-10 text-body-tertiary mt-1">
                      {t.sources.map(s => `${s.coils} from ${s.serial}`).join('  ·  ')}
                      {t.forOrderCode ? `  →  ${t.forOrderCode}` : ''}
                    </div>
                  )}
                </div>
                {t.status !== 'done' && t.status !== 'cancelled' && (
                  <div className="d-flex gap-1">
                    {t.type === 'repack' && (
                      <Button size="sm" variant="success" onClick={() => openRepack(t)}>
                        <FontAwesomeIcon icon={faBoxesStacked} className="me-1" /> Execute
                      </Button>
                    )}
                    {t.status === 'pending' && (
                      <Button size="sm" variant="outline-info" onClick={() => setStatus(t, 'in_progress')} title="Start">
                        <FontAwesomeIcon icon={faPlay} />
                      </Button>
                    )}
                    {t.type !== 'repack' && (
                      <Button size="sm" variant="outline-success" onClick={() => setStatus(t, 'done')} title="Done">
                        <FontAwesomeIcon icon={faCheck} />
                      </Button>
                    )}
                    <Button size="sm" variant="outline-secondary" onClick={() => setStatus(t, 'cancelled')} title="Cancel">
                      <FontAwesomeIcon icon={faXmark} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card.Body>

      <Modal show={repackOpen} onHide={() => setRepackOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Repack pallets {repackTask ? `· ${repackTask.code}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-body-tertiary fs-9">
            Take coils from these pallets to build ONE new pallet. Emptied pallets are consumed.
          </p>
          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            <Table size="sm" hover>
              <thead>
                <tr>
                  <th>Pallet</th>
                  <th>Product</th>
                  <th className="text-end">Avail. coils</th>
                  <th className="text-end">For / stock</th>
                  <th style={{ width: 120 }}>Take coils</th>
                </tr>
              </thead>
              <tbody>
                {pallets.map(p => (
                  <tr key={p.serial}>
                    <td className="font-monospace fs-10">{p.serial}</td>
                    <td>{p.cableName}</td>
                    <td className="text-end">{p.coilCount ?? '—'}</td>
                    <td className="text-end fs-10">
                      {p.customerName ? (
                        <Badge bg="info-subtle" className="text-info-emphasis">{p.customerName}</Badge>
                      ) : (
                        <Badge bg="success-subtle" className="text-success-emphasis">STOCK</Badge>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={0}
                        max={p.coilCount || undefined}
                        value={take[p.serial] || ''}
                        onChange={e => setTake(prev => ({ ...prev, [p.serial]: e.target.value }))}
                      />
                    </td>
                  </tr>
                ))}
                {pallets.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-3">No pallets in stock.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          <hr />
          <div className="d-flex gap-3 flex-wrap align-items-end">
            <Form.Group>
              <Form.Label className="mb-0 fs-10 text-muted">New pallet — customer (blank = stock)</Form.Label>
              <Form.Control size="sm" value={destName} onChange={e => setDestName(e.target.value)} placeholder="Client / blank" />
            </Form.Group>
            <Form.Group>
              <Form.Label className="mb-0 fs-10 text-muted">Order code (optional)</Form.Label>
              <Form.Control size="sm" value={destOrderCode} onChange={e => setDestOrderCode(e.target.value)} placeholder="ORD-…" />
            </Form.Group>
            <Badge bg="primary" className="align-self-center">{takenTotal} coils selected</Badge>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRepackOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={doRepack} disabled={busy || takenTotal <= 0}>
            {busy ? <Spinner animation="border" size="sm" /> : 'Repack → new pallet'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
